import { useState, useEffect } from 'react'
import { useAuthState } from '@/hooks/useAuth'
import { 
  fetchChatsForCurrentUser, 
  getUnreadCountForCurrentUser,
  isCurrentUserArtist
} from '@/lib/chatData'
import { 
  collection, 
  query, 
  where, 
  onSnapshot 
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { Chat } from '@/types/models'

export function useUnreadChatsCount(): { hasUnread: boolean; count: number } {
  const [unreadState, setUnreadState] = useState({ hasUnread: false, count: 0 })
  const { user } = useAuthState()
  const [isArtist, setIsArtist] = useState(false)
  const [chats, setChats] = useState<Chat[]>([])

  // Check artist status
  useEffect(() => {
    if (!user) return
    isCurrentUserArtist().then(setIsArtist)
  }, [user])

  // Calculate unread count whenever chats change
  useEffect(() => {
    if (!user || chats.length === 0) {
      setUnreadState({ hasUnread: false, count: 0 })
      return
    }

    const calculateUnreadCount = async () => {
      try {
        let chatsWithUnread = 0
        
        for (const chat of chats) {
          const unreadCount = await getUnreadCountForCurrentUser(chat as any, user.uid)
          if (unreadCount > 0) {
            chatsWithUnread++
          }
        }
        
        setUnreadState({ 
          hasUnread: chatsWithUnread > 0,
          count: chatsWithUnread
        })
      } catch (error) {
        console.error('Error checking unread chats:', error)
        setUnreadState({ hasUnread: false, count: 0 })
      }
    }

    calculateUnreadCount()
  }, [user, chats, isArtist])

  useEffect(() => {
    if (!user) {
      setChats([])
      return
    }

    const fetchInitialChats = async () => {
      try {
        const fetchedChats = await fetchChatsForCurrentUser()
        setChats(fetchedChats as Chat[])
      } catch (error) {
        console.error('Error fetching chats:', error)
        setChats([])
      }
    }

    fetchInitialChats()

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'chats'),
        where(isArtist ? 'artistId' : 'userId', '==', user.uid)
      ),
      async () => {
        const fetchedChats = await fetchChatsForCurrentUser()
        setChats(fetchedChats as Chat[])
      }
    )

    return () => unsubscribe()
  }, [user, isArtist])

  return unreadState
}
